// src/pages/DoorAdmit.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../services/api";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

const formatDate = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Use a safe generic 18+ DOB: today - 21 years
const defaultDob = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 21);
  return formatDate(d);
})();

export default function DoorAdmit() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [type, setType] = useState("STANDARD");
  const [qty, setQty] = useState(1);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [terminalReady, setTerminalReady] = useState(false);

  // Auto identity for door admissions
  const firstName = "Door Entry";
  const lastName = "Door Entry";
  const dob = defaultDob;

  // Load today's events
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/events/list");
        const todayStr = formatDate(new Date());
        const todays = (data || [])
          .filter((e) => !e.cancelled)
          .filter((e) => String(e.date) === todayStr);
        setEvents(todays);
        if (todays.length === 1) setEventId(String(todays[0].id));
      } catch {
        setEvents([]);
      }
    })();
  }, []);

  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(eventId)),
    [events, eventId]
  );

  const stdLeft = selectedEvent?.remainingStandardTickets ?? null;
  const vipLeft = selectedEvent?.remainingVipTickets ?? null;

  const stdPrice = selectedEvent?.standardPrice ?? selectedEvent?.standardTicketPrice ?? 0;
  const vipPrice = selectedEvent?.vipPrice ?? selectedEvent?.vipTicketPrice ?? 0;
  const unitPrice = type === "STANDARD" ? Number(stdPrice || 0) : Number(vipPrice || 0);
  const total = unitPrice * Number(qty || 0);

  // ---------- CASH ----------
  const admitCash = async () => {
    if (!eventId) return;
    setMsg("");
    setLoading(true);
    try {
      const p = new URLSearchParams({
        eventId,
        type,
        quantity: String(qty),
        firstName,
        lastName,
        paymentMethod: "CASH",
        dateOfBirth: dob,
      });
      const { data } = await api.post(`/api/door/admit?${p.toString()}`);
      bumpRemainingFromResponse(data);
      setMsg(
        `Admitted ${data.created}. Std left: ${data.remainingStandard} | VIP left: ${data.remainingVip}`
      );
      setQty(1);
    } catch (err) {
      setMsg(err?.response?.data || "Admission failed.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- CARD (SIMULATED) ----------
  // Initialize Terminal once
  useEffect(() => {
    if (!window.StripeTerminal) return;
    if (window.__terminal) { setTerminalReady(true); return; }

    const terminal = window.StripeTerminal.create({
      onFetchConnectionToken: async () => {
        const { data } = await api.post("/api/door/terminal/connection-token");
        return data.secret;
      },
      onUnexpectedReaderDisconnect: () => {
        setMsg("Reader disconnected.");
        setTerminalReady(false);
      },
    });

    window.__terminal = terminal;
    setTerminalReady(true);
  }, []);

  // Connect to a simulated reader
  const ensureSimulatedReader = async () => {
    const terminal = window.__terminal;
    if (!terminal) throw new Error("Terminal not initialized");

    if (terminal.getConnectionStatus() === "connected") return;

    const { discoveredReaders, error } = await terminal.discoverReaders({ simulated: true });
    if (error) throw error;
    if (!discoveredReaders || discoveredReaders.length === 0) {
      throw new Error("No simulated readers found");
    }
    const connectRes = await terminal.connectReader(discoveredReaders[0]);
    if (connectRes.error) throw connectRes.error;
  };

  // Collect & process payment, then admit
  const admitCardSimulated = async () => {
    if (!eventId) return;
    if (!unitPrice || !qty) return setMsg("No price/qty selected.");
    if (!window.__terminal) return setMsg("Terminal not initialized.");

    setMsg("");
    setLoading(true);
    try {
      await ensureSimulatedReader();

      // Create a payment intent for the total
      const amountPence = Math.round(total * 100);
      const { data: pi } = await api.post(
        `/api/door/terminal/payment-intent?amountPence=${amountPence}&currency=gbp`
      );

           // 1) Collect on the reader (this returns a PaymentIntent object)
     const { paymentIntent: piAfterCollect, error: collectErr } =
      await window.__terminal.collectPaymentMethod(pi.clientSecret);
      if (collectErr) throw collectErr;

      // 2) Process using the PI you just got back
     const { paymentIntent: piAfterProcess, error: processErr } =
       await window.__terminal.processPayment(piAfterCollect);
      if (processErr) throw processErr;

      // Admit with link to PaymentIntent
      const p = new URLSearchParams({
        eventId,
        type,
        quantity: String(qty),
        firstName,
        lastName,
        paymentMethod: "CARD_TERMINAL",
        dateOfBirth: dob,
        paymentIntentId: piAfterProcess.id,
      });
      const { data } = await api.post(`/api/door/admit?${p.toString()}`);

      bumpRemainingFromResponse(data);
      setMsg(
        `Paid ${fmtGBP(total)} • Admitted ${data.created}. Std left: ${data.remainingStandard} | VIP left: ${data.remainingVip}`
      );
      setQty(1);
    } catch (err) {
      setMsg(err?.message || err?.response?.data || "Card payment failed.");
    } finally {
      setLoading(false);
    }
  };

  const bumpRemainingFromResponse = (data) => {
    setEvents((prev) =>
      prev.map((e) =>
        String(e.id) === String(eventId)
          ? {
              ...e,
              remainingStandardTickets: data?.remainingStandard ?? e.remainingStandardTickets,
              remainingVipTickets: data?.remainingVip ?? e.remainingVipTickets,
            }
          : e
      )
    );
  };

  return (
    <div className="page max-w-md space-y-4">
      <section className="card">
        <h1 className="font-display text-2xl">Door Admit</h1>

        {events.length === 0 && <p className="mt-2 text-slate-300">No events today.</p>}

        <div className="mt-3 grid gap-3">
          <select className="input" value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">Select today’s event</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>{e.title}</option>
            ))}
          </select>

          {selectedEvent && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-slate-400">Standard left</div>
                <div className="font-medium text-white">{stdLeft ?? "—"}</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-slate-400">VIP left</div>
                <div className="font-medium text-white">{vipLeft ?? "—"}</div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="STANDARD">Standard</option>
              <option value="VIP">VIP</option>
            </select>
            <input
              className="input w-24"
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
            />
          </div>

          {/* total */}
          <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">
                {qty} × {type === "STANDARD" ? "Standard" : "VIP"} @ {fmtGBP(unitPrice)}
              </span>
              <span className="font-semibold text-white">Total: {fmtGBP(total || 0)}</span>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <button className="btn-secondary" disabled={!eventId || loading} onClick={admitCash}>
              {loading ? "Working…" : "Admit – Cash"}
            </button>
            <button
              className="btn-primary"
              disabled={!eventId || loading || !terminalReady}
              onClick={admitCardSimulated}
              title={terminalReady ? "Use Stripe Terminal simulator" : "Terminal loading…"}
            >
              {loading ? "Working…" : "Admit – Card (Simulated)"}
            </button>
          </div>
        </div>

        {msg && <p className="mt-3 text-green-200">{msg}</p>}
      </section>
    </div>
  );
}
